export interface TeamMemberDto {
    userId: string;
    fullName: string;
    imageUrl: string;
    role: 'Player' | 'Captain';
    joinedAt: Date;
  }
  
  export interface TeamLinkDto {
    id: string;
    label: string;
    url: string;
    sortOrder: number;
  }
  
  export interface TeamAchievementDto {
    id: string;
    type: string;
    label: string;
    awardedAt: Date;
    metadata: string;
  }
  
  export interface TeamStatsDto {
    totalPlayed: number;
    wins: number;
    losses: number;
    draws: number;
    goalsFor: number;
    goalsAgainst: number;
    tournamentsPlayed: number;
    tournamentsWon: number;
    currentStreak: number;
    streakType: 'Win' | 'Loss' | 'Draw';
    lastFiveResults: string[];
  }
  
  export interface TeamDto {
    id: string;
    name: string;
    sportID: string;
    sportName: string;
    status: 'Active' | 'Inactive' | 'Disbanded';
    latitude?: number;
    longitude?: number;
    description: string;
    adminPhone: string;
    logoUrl: string;
    createdByUserID: string;
    createdAt?: Date;
    teamMembers: TeamMemberDto[];
    teamLinks: TeamLinkDto[];
    teamAchievements: TeamAchievementDto[];
    stats?: TeamStatsDto;
    isCurrentUserMember: boolean;
    isCurrentUserCaptain: boolean;
  }
  
  export interface TeamInvitationDto {
    id: string;
    teamId: string;
    teamName: string;
    teamLogoUrl?: string;
    invitedByUserFullName: string;
    invitedByUserImageUrl: string;
    invitedAt: Date;
    status: string;
  }
  
  export interface InvitableUserDto {
    userId: string;
    fullName: string;
    imageUrl: string;
    isAlreadyMember: boolean;
    isAlreadyInvited: boolean;
  }
  
  export interface CreateTeamForm {
    name: string;
    sportID: string;
    description: string;
    adminPhone: string;
    latitude?: number;
    longitude?: number;
  }

  export type TeamPostContextType = 'Team' | 'TeamMatch';
export type TeamPostVisibility = 'Public' | 'MembersOnly';
export type TeamReactionType = 'Like' | 'Love' | 'Fire' | 'Clap' | 'Sad';
 
export const REACTION_EMOJI: Record<TeamReactionType, string> = {
  Like:  '👍',
  Love:  '❤️',
  Fire:  '🔥',
  Clap:  '👏',
  Sad:   '😢',
};
 
export interface TeamPostReactionSummary {
  type: TeamReactionType;
  count: number;
}
 
export interface TeamPostReplyDto {
  id: string;
  authorId: string;
  authorFullName: string;
  authorImageUrl: string;
  content: string;
  createdAt: Date;
  isDeleted: boolean;
  canDelete: boolean;
  reactions: TeamPostReactionSummary[];
  currentUserReaction: TeamReactionType | null;
}
 
export interface TeamPostDto {
  id: string;
  contextType: TeamPostContextType;
  contextId: string;
  authorId: string;
  authorFullName: string;
  authorImageUrl: string;
  content: string;
  visibility: TeamPostVisibility;
  isPinned: boolean;
  createdAt: Date;
  isDeleted: boolean;
  canDelete: boolean;
  canPin: boolean;
  reactions: TeamPostReactionSummary[];
  currentUserReaction: TeamReactionType | null;
  replyCount: number;
  replies: TeamPostReplyDto[];
}
 
// ── TeamMatch ─────────────────────────────────────────────────────────────────
 
export type TeamMatchType = 'Direct' | 'Open';
export type TeamMatchStatus =
  | 'Pending' | 'Accepted' | 'Rejected'
  | 'Open' | 'TeamApplied'
  | 'Scheduled' | 'Completed' | 'Cancelled';
 
export interface TeamMatchApplicationDto {
  id: string;
  teamId: string;
  teamName: string;
  teamLogoUrl?: string;
  status: 'Pending' | 'Accepted' | 'Rejected';
  appliedAt: Date;
}
 
export interface TeamMemberAvailabilityDto {
  userId: string;
  fullName: string;
  imageUrl: string;
  teamId: string;
  status: 'Pending' | 'Available' | 'Unavailable';
}
 
export interface TeamMatchResultDto {
  id: string;
  scoreA: number;
  scoreB: number;
  reportedByFullName: string;
  reportedAt: Date;
  confirmedAt?: Date;
  status: 'Reported' | 'Confirmed' | 'Disputed' | 'Resolved';
  disputeReason?: string;
  resolutionNote?: string;
}
 
export interface TeamMatchDto {
  id: string;
  teamAId: string;
  teamAName: string;
  teamALogoUrl?: string;
  teamBId?: string;
  teamBName?: string;
  teamBLogoUrl?: string;
  sportID: string;
  sportName?: string;
  type: TeamMatchType;
  status: TeamMatchStatus;
  latitude?: number;
  longitude?: number;
  scheduledAt?: Date;
  createdAt?: Date;
  isCurrentUserTeamA: boolean;
  isCurrentUserTeamB: boolean;
  isCurrentUserCaptainOfAnyTeam: boolean;
  applications: TeamMatchApplicationDto[];
  availabilities: TeamMemberAvailabilityDto[];
  result?: TeamMatchResultDto;
}
 
// ── Team Search ───────────────────────────────────────────────────────────────
 
export interface TeamSearchResultDto {
  id: string;
  name: string;
  sportID: string;
  sportName: string;
  status: string;
  logoUrl?: string;
  description?: string;
  memberCount: number;
  latitude?: number;
  longitude?: number;
  distanceKm?: number;
}

export interface PendingChallengeDto {
  matchId: string;
  challengerTeamId: string;
  challengerTeamName: string;
  challengerTeamLogoUrl?: string;
  challengedTeamId: string;
  challengedTeamName: string;
  challengedTeamLogoUrl?: string;
  scheduledAt?: Date;
  sentAt: Date;
  isCurrentUserCaptain: boolean;
  type: 'ReceivedChallenge' | 'SentChallenge' | 'OpenChallenge' | 'TeamApplied' | 'ActiveDuel' | 'ScheduledMatch';
}